import { Select, TextField, DateRangePicker, AVAILABLE_TIMEZONES, Timestamp, Timezone } from 'vopay';
import type { TransactionHistoryStatus, TransactionTypesOptions } from 'vopay';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Transactions.module.scss';
import { useClientData } from '@/providers/ClientData.provider';
import { Button } from 'UI';

type TransactionSearchParams = {
  type: TransactionTypesOptions;
  status: TransactionHistoryStatus;
  flagged: string;
  searchTerm: string;
  timeRange: [Timestamp, Timestamp];
  timeZone: Timezone;
};

type SearchFormProps = {
  type: TransactionTypesOptions;
  status: TransactionHistoryStatus;
  flagged: string;
  searchTerm: string;
  timeZone: Timezone;
  initialTimeRange: [Timestamp, Timestamp];
  hasInvalidEndDate: boolean;
  shouldShowEndDateError: boolean;
  setShouldShowEndDateError: (event: boolean) => void;

  onSearch: (searchParams: TransactionSearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const TransactionSearchForm: FC = (props: SearchFormProps) => {
  const {
    type,
    status,
    flagged,
    searchTerm,
    initialTimeRange,
    hasInvalidEndDate,
    shouldShowEndDateError,
    timeZone,
    setShouldShowEndDateError,
    onSearch,
  } = props;
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.TransactionHistory.TransactionSearchForm' });
  const { response } = useClientData();
  const [currentType, setCurrentType] = useState<TransactionTypesOptions>(type);
  const [currentStatus, setCurrentStatus] = useState<TransactionHistoryStatus>(status);
  const [currentFlagged, setCurrentFlagged] = useState<string>(flagged);

  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const [timeRange, setTimeRange] = useState<[Timestamp, Timestamp]>(initialTimeRange);
  const [currentTimeZone, setCurrentTimeZone] = useState<Timezone>(timeZone);
  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));
  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];
  const typeOptions: {
    value: TransactionTypesOptions;
    label: string;
  }[] = [
    { value: 'All', label: t('typeOptions.all') },
    { value: 'EFT Withdrawal', label: t('typeOptions.eftWithdrawal') },
    { value: 'EFT Funding', label: t('typeOptions.eftFunding') },
    { value: 'VoPayInstant Withdraw', label: t('typeOptions.voPayInstantWithdraw') },
    { value: 'Refund', label: t('typeOptions.refund') },
    { value: 'Currency Conversion', label: t('typeOptions.currencyConversion') },
    { value: 'Crossborder Withdrawal', label: t('typeOptions.crossBorderWithdrawal') },
    { value: 'Interac Money Request', label: t('typeOptions.interacMoneyRequest') },
    { value: 'Interac Bulk Payout', label: t('typeOptions.interacBulkPayout') },
    { value: 'Inbound e-Transfer', label: t('typeOptions.inboundETransfer') },
    { value: 'Visa Direct Push', label: t('typeOptions.visaDirectPush') },
    { value: 'Credit Card', label: t('typeOptions.creditCard') },
    { value: 'Account Transfer', label: t('typeOptions.accountTransfer') },
    { value: 'Google Pay', label: t('typeOptions.googlePay') },
    { value: 'PayPal Withdrawal', label: t('typeOptions.payPalWithdrawal') },
    { value: 'Reversal', label: t('typeOptions.reversal') },
    { value: 'Bill Pay', label: t('typeOptions.billPay') },
    { value: 'Auto Balance Transfer', label: t('typeOptions.autoBalanceTransfer') },
    { value: 'Virtual Account Deposit', label: t('typeOptions.virtualAccountDeposit') },
    { value: 'Virtual Account Funding', label: t('typeOptions.virtualAccountFunding') },
    { value: 'Virtual Account Withdraw', label: t('typeOptions.virtualAccountWithdraw') },
    { value: 'Virtual Account Wire', label: t('typeOptions.virtualAccountWire') },
    { value: 'Other Funding', label: t('typeOptions.otherFunding') },
    { value: 'Other Withdrawal', label: t('typeOptions.otherWithdrawal') },
  ];
  const typeOptionsUS: {
    value: TransactionTypesOptions;
    label: string;
  }[] = [
    { value: 'All', label: t('typeOptions.all') },
    { value: 'ACH Withdrawal', label: t('typeOptions.achWithdrawal') },
    { value: 'ACH Funding', label: t('typeOptions.achFunding') },
    { value: 'RTP Withdrawal', label: t('typeOptions.rtpWithdrawal') },
    { value: 'Fedwire Withdrawal', label: t('typeOptions.fedwireWithdrawal') },
    { value: 'Fedwire Funding', label: t('typeOptions.fedwireFunding') },
    { value: 'Refund', label: t('typeOptions.refund') },
    { value: 'Currency Conversion', label: t('typeOptions.currencyConversion') },
    { value: 'Visa Direct Push', label: t('typeOptions.visaDirectPush') },
    { value: 'Credit Card', label: t('typeOptions.creditCard') },
    { value: 'Google Pay', label: t('typeOptions.googlePay') },
    { value: 'PayPal Withdrawal', label: t('typeOptions.payPalWithdrawal') },
    { value: 'Venmo Withdrawal', label: t('typeOptions.venmoWithdrawal') },
    { value: 'Zelle Withdrawal', label: t('typeOptions.zelleWithdrawal', { defaultValue: 'Zelle Withdrawal' }) },
    { value: 'Account Transfer', label: t('typeOptions.accountTransfer') },
    { value: 'Reversal', label: t('typeOptions.reversal') },

    { value: 'Auto Balance Transfer', label: t('typeOptions.autoBalanceTransfer') },
    { value: 'Virtual Account Deposit', label: t('typeOptions.virtualAccountDeposit') },
    { value: 'Virtual Account Funding', label: t('typeOptions.virtualAccountFunding') },
    { value: 'Virtual Account Withdraw', label: t('typeOptions.virtualAccountWithdraw') },
    { value: 'Virtual Account Wire', label: t('typeOptions.virtualAccountWire') },
    { value: 'Other Funding', label: t('typeOptions.otherFunding') },
    { value: 'Other Withdrawal', label: t('typeOptions.otherWithdrawal') },
  ];

  const statusOptions: Record<Exclude<TransactionHistoryStatus, null>, string> = {
    All: t('statusOptions.all'),
    requested: t('statusOptions.requested'),
    pending: t('statusOptions.pending'),
    'in progress': t('statusOptions.inProgress'),
    sent: t('statusOptions.sent'),
    successful: t('statusOptions.successful'),
    complete: t('statusOptions.complete'),
    settled: t('statusOptions.settled'),
    failed: t('statusOptions.failed'),
    cancelled: t('statusOptions.cancelled'),
    'cancellation requested': t('statusOptions.cancellationRequested'),
    'pending approval': t('statusOptions.pendingApproval'),
    waiting: t('statusOptions.waiting'),
  };

  const flaggedOptions = {
    All: t('flaggedOptions.all'),
    Yes: t('flaggedOptions.yes'),
    No: t('flaggedOptions.no'),
  };

  return (
    <div className={styles.searchControls}>
      <DateRangePicker
        onChange={(range) => {
          setTimeRange(range); // Update local state for time range
        }}
        initialTime={timeRange}
        triggerVariant="outlineShadow"
        hasInvalidEndDate={hasInvalidEndDate}
        shouldShowEndDateError={shouldShowEndDateError}
        setShouldShowEndDateError={setShouldShowEndDateError}
      />

      <Select
        version="2"
        placeholder={t('type') || undefined}
        options={response.Country === 'US' ? typeOptionsUS : typeOptions}
        name="type"
        value={currentType}
        onBlur={() => {}}
        onChange={(_, obj) => {
          setCurrentType(obj.data?.value || (obj.value as TransactionTypesOptions));
        }}
        sort={false}
      />

      <Select
        version="2"
        placeholder={t('status') || undefined}
        options={convertToOptions(statusOptions)}
        name="status"
        value={currentStatus || ''}
        onBlur={() => {}}
        onChange={(e) => {
          setCurrentStatus(e.target.value as TransactionHistoryStatus);
        }}
        sort={false}
      />

      <Select
        version="2"
        placeholder={t('flagged') || undefined}
        options={convertToOptions(flaggedOptions)}
        name="flagged"
        value={currentFlagged}
        onBlur={() => {}}
        onChange={(e) => {
          setCurrentFlagged(e.target.value);
        }}
        sort={false}
      />

      <Select
        version="2"
        placeholder={t('timeZone') || undefined}
        options={timeZoneOptions}
        name="timeZone"
        value={currentTimeZone}
        onBlur={() => {}}
        filterable
        onChange={(e) => {
          setCurrentTimeZone(e.target.value as Timezone);
        }}
        sort={false}
      />

      <TextField
        key="TransactionSearchKeyword"
        name="TransactionSearchKeyword"
        placeholder={t('keyword') || ''}
        value={currentSearchTerm}
        onBlur={() => {}}
        onChange={(e) => {
          setCurrentSearchTerm(e.target.value);
        }}
        variant="outlineShadow"
      />

      <Button
        id="search-transaction-history"
        size="small"
        colorScheme="primary"
        onClick={() => {
          onSearch({
            type: currentType,
            status: currentStatus,
            flagged: currentFlagged,
            searchTerm: currentSearchTerm,
            timeRange,
            timeZone: currentTimeZone,
          });
        }}
      >
        {t('search')}
      </Button>
    </div>
  );
};
// The map is keyed by the raw API value (TransactionType, status, currency) with the
// translated display text as its value, so the option takes its label from the value and
// its submitted value from the key. Matches convertToOptions in the vopay package.
function convertToOptions(options: { [key: string]: string }) {
  return Object.entries(options).map(([key, value]) => ({
    label: value,
    value: key,
  }));
}

export default TransactionSearchForm;
